import { useCallback, useState } from 'react'
import { ReactComponent as CloseIcon } from 'assets/icons/close.svg'
import ErrorBoundary from 'components/ErrorBoundary'
import { Pane, PaneContents } from 'components/PageStructure'
import { ToolButton } from 'components/SideToolBar'
import { Tab, Tabs } from 'grommet'
import { useBulkActionsHistory, useRefetchSourceQueueOnCompletion } from 'hooks/useBulkActions'
import usePane from 'hooks/usePane'
import { Layers } from 'react-feather'
import styled, { css } from 'styled-components'
import { primaryColor, bodyPrimaryColor } from 'styles/styleVariables'
import HistoryTab from './HistoryTab'
import NewTab from './NewTab/index'

const NEW_TAB_INDEX = 0
const HISTORY_TAB_INDEX = 1
const POLL_INTERVAL_MS = 3000

export const BulkActionPanel = (): JSX.Element => {
  const { paneVisible, firstLoad, hidePane } = usePane('BulkAction')
  const [activeTab, setActiveTab] = useState(NEW_TAB_INDEX)
  const { bulkActions, loading, loadingMore, error, locationId, startPolling, loadMore, hasMore } =
    useBulkActionsHistory()
  useRefetchSourceQueueOnCompletion(bulkActions)

  const handleCreateSuccess = useCallback(() => {
    setActiveTab(HISTORY_TAB_INDEX)
    startPolling(POLL_INTERVAL_MS)
  }, [startPolling])

  const panels = [
    {
      title: 'New',
      content: (
        <TabContent>
          <NewTab onCreateSuccess={handleCreateSuccess} />
        </TabContent>
      ),
    },
    {
      title: 'History',
      content: (
        <TabContent $scrollable>
          <HistoryTab
            bulkActions={bulkActions}
            loading={loading}
            loadingMore={loadingMore}
            error={error}
            locationId={locationId}
            loadMore={loadMore}
            hasMore={hasMore}
          />
        </TabContent>
      ),
    },
  ]

  return (
    <ErrorBoundary>
      <Pane id="BulkActionPane" firstLoad={firstLoad} visible={paneVisible}>
        <PaneContents
          styles={css`
            height: 94vh !important;
          `}
        >
          <CloseButton data-testid="bulk-action-close" label="Close" icon={CloseIcon} clickCallback={hidePane} />
          <PaneTitle>
            <Layers size={18} stroke={bodyPrimaryColor} />
            Bulk Actions
          </PaneTitle>

          <StyledTabs activeIndex={activeTab} onActive={setActiveTab}>
            {panels.map(panel => (
              <StyledTab key={panel.title} title={panel.title}>
                {panel.content}
              </StyledTab>
            ))}
          </StyledTabs>
        </PaneContents>
      </Pane>
    </ErrorBoundary>
  )
}

const PaneTitle = styled.h3`
  display: flex;
  font-size: 1.125rem;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
  > svg {
    margin-right: 0.5rem;
  }
`

const CloseButton = styled(ToolButton)`
  border: none;
  position: absolute;
  right: 1rem;
  height: 2rem;
  width: 2rem;
  :hover,
  :focus {
    box-shadow: none !important;
    cursor: pointer;
  }
`

const StyledTabs = styled(Tabs)`
  margin-top: 0.5rem;
  align-items: stretch;

  > div[role='tablist'] {
    width: 100%;
  }

  > div[role='tabpanel'] {
    flex: 1;
    display: flex;
  }
`

const StyledTab = styled(Tab)<{ active?: boolean }>`
  background-color: transparent;
  justify-content: center;
  flex: 1;
  > div {
    padding: 0.5rem 1rem;
    background-color: transparent;
    align-items: center;
    > span {
      font-size: 14px;
      font-weight: 600;
      color: ${({ active }) => (active ? primaryColor : bodyPrimaryColor)};
      ${({ active }) => active && `border-bottom: 3px solid ${primaryColor};`}
    }
  }
`

const TabContent = styled.div<{ $scrollable?: boolean }>`
  width: 100%;
  overflow-y: ${({ $scrollable }) => ($scrollable ? 'auto' : 'visible')};
  overflow-x: ${({ $scrollable }) => ($scrollable ? 'hidden' : 'visible')};
  padding-top: 0.75rem;
`
